import { Check } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const SETUP = [
  "Website design and initial build",
  "Up to 5 core pages",
  "Mobile optimization",
  "Basic SEO setup",
  "Contact/quote form",
  "Initial business content setup",
  "Domain connection assistance",
];

const MONTHLY = [
  "Hosting",
  "SSL/security",
  "Backups",
  "Routine maintenance",
  "Performance monitoring",
  "Reasonable small content updates",
  "Technical support",
];

const scrollToForm = (e) => {
  const el = document.querySelector("#lead-form");
  if (el) {
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const Pricing = () => {
  return (
    <section
      id="pricing"
      className="bg-goc-light"
      data-testid="section-pricing"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
        <SectionHeading
          eyebrow="Pricing"
          title="Simple, Affordable Pricing"
          subtitle="One setup fee and a simple monthly plan. No long-term contract."
          testId="heading-pricing"
        />

        <div className="max-w-3xl mx-auto">
          <div
            className="relative bg-white border border-goc-border rounded-2xl shadow-sm overflow-hidden"
            data-testid="pricing-card"
          >
            <div className="absolute top-0 right-0 bg-goc-primary text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg tracking-widest uppercase">
              Founding Offer
            </div>

            <div className="p-6 md:p-10">
              <h3 className="text-2xl md:text-3xl font-bold text-goc-text">
                Founding Customer Website
              </h3>
              <p className="mt-2 text-sm text-goc-muted">
                Everything you need to look professional online.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="rounded-xl bg-goc-light border border-goc-border p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-goc-muted">
                    One-Time Setup
                  </p>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-4xl font-extrabold text-goc-text">
                      $299
                    </span>
                    <span className="text-sm text-goc-muted">one-time</span>
                  </div>
                </div>
                <div className="rounded-xl bg-goc-navy text-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
                    Website Service
                  </p>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-4xl font-extrabold">$39</span>
                    <span className="text-sm text-white/80">/ month</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-widest text-goc-text">
                    Setup includes
                  </h4>
                  <ul className="mt-3 space-y-2.5">
                    {SETUP.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2.5 text-sm text-goc-text"
                        data-testid={`pricing-setup-${f
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 text-goc-primary shrink-0"
                          strokeWidth={3}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-widest text-goc-text">
                    Monthly includes
                  </h4>
                  <ul className="mt-3 space-y-2.5">
                    {MONTHLY.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2.5 text-sm text-goc-text"
                        data-testid={`pricing-monthly-${f
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 text-goc-primary shrink-0"
                          strokeWidth={3}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-goc-border">
                <p className="text-sm font-medium text-goc-text">
                  No long-term monthly contract. Cancel anytime.
                </p>
                <a
                  href="#lead-form"
                  onClick={scrollToForm}
                  className="mt-4 inline-flex w-full sm:w-auto items-center justify-center rounded-lg bg-goc-primary px-6 py-3.5 text-base font-semibold text-white hover:bg-goc-deep transition-colors shadow-sm shadow-goc-primary/30"
                  data-testid="pricing-cta-get-my-website"
                >
                  Get My Website
                </a>
                <p className="mt-4 text-xs text-goc-muted leading-relaxed">
                  Setup fee covers the initial website build. Monthly service
                  is required for hosting, maintenance, and continued website
                  service. Full service terms are provided before payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
